import React from 'react';
import { Settings, Save, CheckCircle, Trash2, Printer, Zap } from 'lucide-react';

const TimetableHeader = ({
  academicYear,
  setAcademicYear,
  selectedClass,
  setSelectedClass,
  availableClasses = [],
  selectedSection,
  setSelectedSection,
  availableSections = [],
  status = 'DRAFT',
  onOpenBellSchedule,
  onAutoGenerate,
  onClearDraft,
  onSaveDraft,
  onPublish,
  onToggleEdit,
  onPrint,
  saving = false,
  publishing = false,
  readOnly = false
}) => {
  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '14px', 
      padding: '18px 22px', 
      marginBottom: '20px', 
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', 
      border: '1px solid #E2E8F0', 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: '16px', 
      justify: 'space-between', 
      alignItems: 'center' 
    }}>
      
      {/* LEFT DROPDOWNS: ACADEMIC YEAR, CLASS, SECTION */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        <div>
          <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
            Academic Year
          </label>
          <select 
            className="form-control"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            style={{ height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', fontWeight: '600', padding: '0 12px' }}
          >
            <option value="2026-2027">2026-2027</option>
            <option value="2025-2026">2025-2026</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
            Class / Grade
          </label>
          <select 
            className="form-control"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            style={{ height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', fontWeight: '600', padding: '0 12px' }}
          >
            {availableClasses.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '4px', display: 'block' }}>
            Section
          </label>
          <select 
            className="form-control"
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            style={{ height: '38px', borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '13px', fontWeight: '600', padding: '0 12px' }}
          >
            {availableSections.map(sec => (
              <option key={sec} value={sec}>Section {sec}</option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: '16px' }}>
          <span style={{ 
            padding: '5px 12px', 
            borderRadius: '20px', 
            fontSize: '12px', 
            fontWeight: '700',
            backgroundColor: status === 'PUBLISHED' ? '#DCFCE7' : status === 'ARCHIVED' ? '#F1F5F9' : '#DBEAFE',
            color: status === 'PUBLISHED' ? '#166534' : status === 'ARCHIVED' ? '#475569' : '#1E40AF',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            ● {status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT IN PROGRESS'}
          </span>
        </div>
      </div>

      {/* RIGHT ACTIONS WITH REFINED USER-FRIENDLY TERMINOLOGY */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button 
          type="button" 
          onClick={onOpenBellSchedule}
          style={{ background: 'white', border: '1px solid #CBD5E1', color: '#334155', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          title="Configure period start and end timings"
        >
          <Settings size={16} /> School Period Times
        </button>

        {readOnly ? (
          <>
            <button 
              type="button" 
              onClick={onToggleEdit}
              style={{ background: '#3B82F6', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 6px rgba(59, 130, 246, 0.3)' }}
              title="Unlock this published timetable to edit periods or auto-generate schedule"
            >
              ✏️ Edit / Unlock Timetable
            </button>

            <button 
              type="button" 
              onClick={onAutoGenerate}
              style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
              title="Automatically builds the weekly timetable using subject requirements, assigned teachers, available periods, and scheduling rules."
            >
              <Zap size={16} /> Re-Generate Timetable
            </button>

            <button 
              type="button" 
              onClick={onPrint || (() => window.print())}
              style={{ background: '#64748B', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Printer size={16} /> Print Timetable
            </button>
          </>
        ) : (
          <>
            <button 
              type="button" 
              onClick={onAutoGenerate}
              style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
              title="Automatically builds the weekly timetable using subject requirements, assigned teachers, available periods, and scheduling rules."
            >
              <Zap size={16} /> Generate Timetable Automatically
            </button>

            <button 
              type="button" 
              onClick={onClearDraft}
              style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#DC2626', padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Reset all period assignments in this draft"
            >
              <Trash2 size={16} /> Clear Timetable
            </button>

            <button 
              type="button" 
              onClick={onSaveDraft}
              disabled={saving}
              style={{ background: 'white', border: '1px solid #2563EB', color: '#2563EB', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Save current progress and continue editing later"
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save & Continue Later'}
            </button>

            <button 
              type="button" 
              onClick={onPublish}
              disabled={publishing}
              style={{ background: '#2563EB', border: 'none', color: 'white', padding: '8px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)' }}
              title="Review and publish the final timetable"
            >
              <CheckCircle size={16} /> {publishing ? 'Publishing...' : 'Publish Timetable'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TimetableHeader;
